import React, { useEffect, useState } from "react"
import { Location } from "@root/types/global"
import {
  ProductQuickView,
  ProductQuickViewBody,
  ProductQuickViewCarousel,
  ProductQuickViewForm,
  ProductQuickViewHeader,
  ProductQuickViewTitle,
  ProductQuickViewFooter,
} from "@components/ProductQuickView"
import { useCart } from "@hooks/useCart"
import { useAppContext } from "@providers/app"
import { useShopify } from "@hooks/useShopify"
import sanityClient from "@sanity/client"

import config from "../../../config"
import { ClickAndCollectProvider } from "@app/providers/clickAndCollect"

export interface ProductCardDrawerProps {
  product: BundleProduct
  location: Location
  onClose: VoidFunction
}

const ProductCardDrawer = ({ product, location, onClose, setQuantity }: ProductCardDrawerProps) => {
  const [showForm, setShowForm] = useState<boolean>(false)
  const { getProducts } = useShopify()
  const { addToCart, loading: cartLoading } = useCart()
  const { activeVariant, dispatch } = useAppContext()
  const [shopifyProduct, setShopifyProduct] = useState<any>([])
  const [sanityProduct, setSanityProduct] = useState<any>([])

  const client = sanityClient({
    dataset: config.store.sanityDataset,
    projectId: config.store.sanityProjectId,
    apiVersion: config.store.sanityApiVersion,
    token: config.store.sanityToken,
    useCdn: true,
  })

  const triggerMiniCart = () => {
    dispatch({
      type: "cart",
      payload: {
        activeCart: true,
        addedToCart: true,
      },
    })
  }

  const getShopifyProduct = async (handle: string) => {
    if (!handle) return
    const data = await getProducts({ firstVariants: 50, firstImages: 10, handles: [handle] })
    if (data?.[0]) {
      setShopifyProduct(data?.[0])
      setShowForm(true)
    }
  }

  const getSanityProduct = React.useCallback(async (handle: string) => {
    const query = `*[_type == "product" && shopify.handle == "${handle}"][0] {
      siblings[]-> {
        title,
        tags,
        images,
        shopify {
          active,
          published,
          handle,
          raw
        }
      }
    }`
    const data = await client.fetch(query)
    if (data) setSanityProduct(data)
  }, [])

  useEffect(() => {
    if (product?.handle) {
      getShopifyProduct(product?.handle)
      getSanityProduct(product?.handle)
    }
  }, [])

  const handleAddToCart = (_product: any, variant: any, quantity: number) =>
    addToCart(_product, activeVariant?.id ? activeVariant : variant?.id ? variant : {}, quantity).then(() => {
      onClose()
      triggerMiniCart()
    })

  const productSku = shopifyProduct?.variants?.[0]?.sku

  return (
    <ProductQuickView product={shopifyProduct} location={location} onClose={onClose} onSubmit={handleAddToCart} loading={cartLoading}>
      <ClickAndCollectProvider productSkus={[productSku]} productCount={1}>
        <ProductQuickViewHeader />
        <ProductQuickViewBody>
          <ProductQuickViewCarousel />
          <ProductQuickViewTitle />
          {showForm && (
            <ProductQuickViewForm sanityProduct={sanityProduct} updateQuickViewProduct={setShopifyProduct} setGRQuantity={setQuantity} />
          )}
          <ProductQuickViewFooter />
        </ProductQuickViewBody>
      </ClickAndCollectProvider>
    </ProductQuickView>
  )
}

export default ProductCardDrawer
